import { useState } from 'react';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Combobox } from '@Components/Combobox';
import { FilterClause, SearchFieldDescriptor } from '@Hooks/useOmniSearch';
import { FacetCombobox } from './FacetCombobox';
import { dateToUnix, getClauseValue, setClause, unixToDateInput } from './utils';

const ANY = '__any';

interface FieldProps {
  index: string;
  field: SearchFieldDescriptor;
  clauses: FilterClause[];
  onChange: (next: FilterClause[]) => void;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="mb-1 block text-xs font-medium text-zinc-500">{children}</span>;
}

function EnumField({ index, field, clauses, onChange }: FieldProps) {
  const current = getClauseValue(clauses, field.name, 'eq');
  return (
    <label className="block">
      <FieldLabel>{field.label}</FieldLabel>
      <FacetCombobox
        index={index}
        field={field.name}
        label={field.label}
        value={current != null ? String(current) : undefined}
        onChange={(v) => onChange(setClause(clauses, field.name, 'eq', v))}
        className="w-52"
      />
    </label>
  );
}

const BOOLEAN_ITEMS = [
  { value: ANY, label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

function BooleanField({ field, clauses, onChange }: FieldProps) {
  const current = getClauseValue(clauses, field.name, 'eq');
  const value = current === true ? 'true' : current === false ? 'false' : ANY;
  return (
    <label className="block">
      <FieldLabel>{field.label}</FieldLabel>
      <div className="w-40">
        <Combobox
          items={BOOLEAN_ITEMS}
          value={value}
          placeholder="Any"
          onChange={(v) =>
            onChange(setClause(clauses, field.name, 'eq', v === ANY ? undefined : v === 'true'))
          }
        />
      </div>
    </label>
  );
}

function NumberField({ field, clauses, onChange }: FieldProps) {
  const min = getClauseValue(clauses, field.name, 'gte');
  const max = getClauseValue(clauses, field.name, 'lte');
  return (
    <div>
      <FieldLabel>{field.label}</FieldLabel>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          placeholder="Min"
          defaultValue={min != null ? String(min) : ''}
          className="w-24"
          onBlur={(e) =>
            onChange(
              setClause(clauses, field.name, 'gte', e.target.value === '' ? undefined : Number(e.target.value)),
            )
          }
        />
        <span className="text-zinc-400">–</span>
        <Input
          type="number"
          placeholder="Max"
          defaultValue={max != null ? String(max) : ''}
          className="w-24"
          onBlur={(e) =>
            onChange(
              setClause(clauses, field.name, 'lte', e.target.value === '' ? undefined : Number(e.target.value)),
            )
          }
        />
      </div>
    </div>
  );
}

const DATE_MODE_ITEMS = [
  { value: 'absolute', label: 'Between' },
  { value: 'relative', label: 'Relative' },
];

const RELATIVE_DIRECTION_ITEMS = [
  { value: 'within_last', label: 'Within last' },
  { value: 'older_than', label: 'Older than' },
];

const UNIT_ITEMS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

// Every op that owns this date field, so switching modes/direction leaves no stale clause.
const DATE_OPS: FilterClause['op'][] = ['gte', 'lte', 'within_last', 'older_than'];

function DateField({ field, clauses, onChange }: FieldProps) {
  const relativeClause = clauses.find(
    (c) => c.field === field.name && (c.op === 'within_last' || c.op === 'older_than'),
  );
  const [mode, setMode] = useState<'absolute' | 'relative'>(
    relativeClause ? 'relative' : 'absolute',
  );

  const stripDate = () => clauses.filter((c) => !(c.field === field.name && DATE_OPS.includes(c.op)));

  if (mode === 'relative') {
    const direction = relativeClause?.op ?? 'within_last';
    const window = (relativeClause?.value ?? {}) as { amount?: number; unit?: string };
    const setRelative = (nextDirection: string, nextWindow: { amount?: number; unit?: string }) => {
      const base = stripDate();
      if (nextWindow.amount == null || !nextWindow.unit) {
        onChange(base);
        return;
      }
      onChange([...base, { field: field.name, op: nextDirection as FilterClause['op'], value: nextWindow as any }]);
    };
    return (
      <div>
        <FieldLabel>{field.label}</FieldLabel>
        <div className="flex items-center gap-1">
          <div className="w-28">
            <Combobox items={DATE_MODE_ITEMS} value="relative" onChange={(v) => v === 'absolute' && setMode('absolute')} />
          </div>
          <div className="w-32">
            <Combobox
              items={RELATIVE_DIRECTION_ITEMS}
              value={direction}
              onChange={(v) => setRelative(v, window)}
            />
          </div>
          <Input
            type="number"
            min={1}
            placeholder="Amount"
            defaultValue={window.amount != null ? String(window.amount) : ''}
            className="w-24"
            onBlur={(e) =>
              setRelative(direction, {
                ...window,
                amount: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
          <div className="w-28">
            <Combobox
              items={UNIT_ITEMS}
              value={window.unit ?? ''}
              placeholder="Unit"
              onChange={(v) => setRelative(direction, { ...window, unit: v })}
            />
          </div>
        </div>
      </div>
    );
  }

  const from = getClauseValue(clauses, field.name, 'gte');
  const to = getClauseValue(clauses, field.name, 'lte');
  return (
    <div>
      <FieldLabel>{field.label}</FieldLabel>
      <div className="flex items-center gap-1">
        <div className="w-28">
          <Combobox
            items={DATE_MODE_ITEMS}
            value="absolute"
            onChange={(v) => {
              if (v === 'relative') {
                onChange(stripDate());
                setMode('relative');
              }
            }}
          />
        </div>
        <Input
          type="date"
          defaultValue={unixToDateInput(from)}
          className="w-40"
          onChange={(e) =>
            onChange(
              setClause(clauses, field.name, 'gte', e.target.value === '' ? undefined : dateToUnix(e.target.value)),
            )
          }
        />
        <span className="text-zinc-400">–</span>
        <Input
          type="date"
          defaultValue={unixToDateInput(to)}
          className="w-40"
          onChange={(e) =>
            onChange(
              setClause(clauses, field.name, 'lte', e.target.value === '' ? undefined : dateToUnix(e.target.value, true)),
            )
          }
        />
      </div>
    </div>
  );
}

function StringField({ field, clauses, onChange }: FieldProps) {
  const current = getClauseValue(clauses, field.name, 'eq');
  const [draft, setDraft] = useState(current != null ? String(current) : '');
  const commit = () =>
    onChange(setClause(clauses, field.name, 'eq', draft.trim() === '' ? undefined : draft.trim()));
  return (
    <label className="block">
      <FieldLabel>{field.label} (exact)</FieldLabel>
      <Input
        placeholder={`Exact ${field.label.toLowerCase()}…`}
        value={draft}
        className="w-52"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
      />
    </label>
  );
}

export function OmniFilterBar({
  index,
  fields,
  value,
  onChange,
}: {
  index: string;
  fields: SearchFieldDescriptor[];
  value: FilterClause[];
  onChange: (next: FilterClause[]) => void;
}) {
  if (!fields.length) return null;
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3">
      {fields.map((field) => {
        const props: FieldProps = { index, field, clauses: value, onChange };
        switch (field.type) {
          case 'enum':
            return <EnumField key={field.name} {...props} />;
          case 'boolean':
            return <BooleanField key={field.name} {...props} />;
          case 'number':
            return <NumberField key={field.name} {...props} />;
          case 'date':
            return <DateField key={field.name} {...props} />;
          default:
            return <StringField key={field.name} {...props} />;
        }
      })}
    </div>
  );
}
